import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const usePushNotifications = () => {
  const { user, isAuthenticated } = useAuth();
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated || !user) return;
    if (registeredRef.current) return;

    const setup = async () => {
      try {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') {
          console.log('[Push] Permission denied');
          return;
        }

        await PushNotifications.register();
        registeredRef.current = true;
      } catch (err) {
        console.error('[Push] Setup failed:', err);
      }
    };

    // Token received from FCM
    PushNotifications.addListener('registration', async (token) => {
      console.log('[Push] FCM token:', token.value?.substring(0, 20) + '...');
      try {
        await api.post('/push-tokens', { token: token.value, platform: 'android' });
      } catch (err) {
        console.error('[Push] Token registration failed:', err);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[Push] Registration error:', err);
    });

    // Push received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Foreground notification:', notification.title);
    });

    // User tapped a push notification
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data;
      if (data?.url) {
        window.location.hash = data.url;
      }
    });

    setup();

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [isAuthenticated, user]);

  // Unregister token on logout
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated && registeredRef.current) {
      registeredRef.current = false;
    }
  }, [isAuthenticated]);
};

export default usePushNotifications;
